"use client";

import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import { StarIcon } from "@heroicons/react/24/solid";
import Image from "next/image";
import { ComponentPropsWithoutRef, FC } from "react";
import { useMessages } from "../context/i18n";
import { rivalPage as enRivalPage } from "../dictionaries/en/rival-page";
import Typography from "./Typography";

export type AlternativeTestimonial = {
  name: string;
  role?: string;
  quote: string;
  rating?: number;
  platform: "g2" | "capterra" | "trustpilot" | "youtube";
  sourceUrl: string;
  avatarUrl?: string;
};

type AlternativeTestimonialsSectionProps = {
  testimonials: AlternativeTestimonial[];
  title?: string;
} & ComponentPropsWithoutRef<"section">;

// Display label for each review-platform source. Kept out of the data file so
// every testimonial just references the `platform` key and gets the same wording
// / a11y label everywhere.
const PLATFORM_LABELS: Record<AlternativeTestimonial["platform"], string> = {
  g2: "G2",
  capterra: "Capterra",
  trustpilot: "Trustpilot",
  youtube: "YouTube",
};

const AlternativeTestimonialsSection: FC<AlternativeTestimonialsSectionProps> =
  ({ testimonials, title, ...rest }) => {
    const m = useMessages("rivalPage", enRivalPage);
    return (
      <section {...rest} className={`${rest.className ?? ""}`}>
        <Typography variant="h2" component="h2" className="text-center">
          {title ?? m.testimonialsTitle}
        </Typography>
        <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {testimonials.map((testimonial) => (
            <TestimonialCard
              key={testimonial.sourceUrl}
              testimonial={testimonial}
            />
          ))}
        </div>
      </section>
    );
  };

const TestimonialCard: FC<{ testimonial: AlternativeTestimonial }> = ({
  testimonial,
}) => {
  const m = useMessages("rivalPage", enRivalPage);
  const platform = PLATFORM_LABELS[testimonial.platform];
  const reviewedLabel = (
    testimonial.platform === "youtube" ? m.watchReview : m.viewReview
  ).replace("{platform}", platform);
  return (
    <article className="flex flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex items-center gap-3">
        <TestimonialAvatar
          name={testimonial.name}
          role={testimonial.role}
          avatarUrl={testimonial.avatarUrl}
        />
        <div className="min-w-0">
          <Typography
            variant="subtitle3"
            component="p"
            className="truncate text-slate-900"
          >
            {testimonial.name}
          </Typography>
          {testimonial.role && (
            <Typography
              variant="caption"
              component="p"
              className="truncate text-slate-500"
            >
              {testimonial.role}
            </Typography>
          )}
        </div>
      </div>
      {typeof testimonial.rating === "number" && (
        <StarRow
          rating={testimonial.rating}
          platform={platform}
          className="mt-4"
        />
      )}
      <blockquote className="mt-4 flex-1">
        <Typography variant="body2" component="p" className="text-slate-700">
          {`"${testimonial.quote}"`}
        </Typography>
      </blockquote>
      <a
        href={testimonial.sourceUrl}
        target="_blank"
        rel="noopener nofollow"
        className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-violet-600 hover:text-violet-700"
        aria-label={reviewedLabel}
      >
        {reviewedLabel}
        <ArrowTopRightOnSquareIcon
          className="h-4 w-4"
          strokeWidth={2}
          aria-hidden="true"
        />
      </a>
    </article>
  );
};

const TestimonialAvatar: FC<{
  name: string;
  role?: string;
  avatarUrl?: string;
}> = ({ name, role, avatarUrl }) => {
  const m = useMessages("rivalPage", enRivalPage);
  const alt = (role ? m.userWithRoleAlt : m.userAlt)
    .replace("{name}", name)
    .replace("{role}", role ?? "");
  if (avatarUrl) {
    return (
      <Image
        src={avatarUrl}
        alt={alt}
        width={48}
        height={48}
        className="h-12 w-12 shrink-0 rounded-full object-cover"
      />
    );
  }
  return (
    <div
      role="img"
      aria-label={alt}
      className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-violet-100 text-violet-700 font-semibold"
    >
      {initials(name)}
    </div>
  );
};

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

const StarRow: FC<{
  rating: number;
  platform: string;
  className?: string;
}> = ({ rating, platform, className }) => {
  const m = useMessages("rivalPage", enRivalPage);
  const widthPercent = Math.max(0, Math.min(5, rating)) * 20;
  return (
    <span
      role="img"
      aria-label={m.rated
        .replace("{rating}", String(rating))
        .replace("{platform}", platform)}
      className={`relative inline-flex ${className ?? ""}`}
    >
      <span className="flex text-slate-200">
        {[0, 1, 2, 3, 4].map((i) => (
          <StarIcon key={i} className="h-4 w-4" aria-hidden="true" />
        ))}
      </span>
      <span
        className="absolute inset-0 flex overflow-hidden text-amber-400"
        style={{ width: `${widthPercent}%` }}
        aria-hidden="true"
      >
        {[0, 1, 2, 3, 4].map((i) => (
          <StarIcon key={i} className="h-4 w-4 shrink-0" />
        ))}
      </span>
    </span>
  );
};

export default AlternativeTestimonialsSection;
